import { useCallback, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_ORG_RECENT_ACTIVITIES } from '../../../lib/graphql/queries/organizations';

const PAGE_SIZE = 20;

type OrgRecentActivitiesData = { organization?: { recentActivities?: { items?: unknown[]; hasMore?: boolean } } };

export const useOrgRecentActivitiesGQL = ({
  enabled = true,
  pageSize = PAGE_SIZE,
  eventTypes,
  channels,
}: {
  enabled?: boolean;
  pageSize?: number;
  /** Server-side filter on event_type — e.g. ['call'] or ['property_view', 'valuation']. Omit for all. */
  eventTypes?: string[];
  /** Server-side filter on channel — e.g. ['email']. Omit for all. */
  channels?: string[];
} = {}) => {
  const { organization } = useOrganizationStore();
  const orgId = organization?.id ?? '';
  const [, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const { data, loading, fetchMore, refetch } = useQuery<OrgRecentActivitiesData>(GET_ORG_RECENT_ACTIVITIES, {
    variables: {
      organizationId: orgId,
      limit: pageSize,
      offset: 0,
      sortOrder: 'DESC',
      eventTypes: eventTypes && eventTypes.length > 0 ? eventTypes : undefined,
      channels: channels && channels.length > 0 ? channels : undefined,
    },
    skip: !enabled || !orgId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });

  const activities = data?.organization?.recentActivities?.items ?? [];
  const hasMore = data?.organization?.recentActivities?.hasMore ?? false;

  const loadMore = useCallback(async () => {
    const newOffset = activities.length;
    setOffset(newOffset);
    setLoadingMore(true);
    try {
      await fetchMore({
        variables: { offset: newOffset },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          const p = prev as OrgRecentActivitiesData;
          const r = fetchMoreResult as OrgRecentActivitiesData;
          return {
            organization: {
              ...r.organization,
              recentActivities: {
                ...r.organization?.recentActivities,
                items: (() => {
                  const prev = p.organization?.recentActivities?.items ?? [];
                  const next = r.organization?.recentActivities?.items ?? [];
                  const seen = new Set(prev.map((i: any) => i.eventId));
                  return [...prev, ...next.filter((i: any) => !seen.has(i.eventId))];
                })(),
              },
            },
          };
        },
      });
    } finally {
      setLoadingMore(false);
    }
  }, [activities.length, fetchMore]);

  const refresh = useCallback(async () => {
    setOffset(0);
    await refetch({ offset: 0 });
  }, [refetch]);

  return {
    activities,
    hasMore,
    isLoading: loading,
    isLoadingMore: loadingMore,
    loadMore,
    refresh,
  };
};
